<script lang="ts">
  import { onDestroy } from 'svelte'
  import type { Token } from 'marked'
  import { marked } from 'marked'
  import SvelteMarkdown from '@humanspeak/svelte-markdown'
  import {
    fetchNovelTranslation,
    getCachedNovelTranslation,
    getNovelTranslationJobStatus,
    invalidateNovelTranslationPage,
    type NovelTranslateQueueStatus,
  } from '$lib/novel-translate-cache'
  import { Spinner } from '$lib/components/ui/spinner/index.js'
  import { Progress } from '$lib/components/ui/progress/index.js'
  import LanguagesIcon from '@lucide/svelte/icons/languages'
  import AlertCircleIcon from '@lucide/svelte/icons/alert-circle'
  import Text from '$lib/components/nuread/renderers/Text.svelte'
  import Paragraph from '$lib/components/nuread/renderers/Paragraph.svelte'
  import Link from '$lib/components/nuread/renderers/Link.svelte'
  import Heading from '$lib/components/nuread/renderers/Heading.svelte'
  import List from '$lib/components/nuread/renderers/List.svelte'
  import ListItem from '$lib/components/nuread/renderers/ListItem.svelte'
  import ImageRender from '$lib/components/nuread/renderers/Image.svelte'

  interface Props {
    server: any
    bookId: string | undefined
    seriesId: string
    type: string
    num: string
    subNum: string
    pageNum: number
    originalTokens: Token[]
    translateEnabled?: boolean
    retranslateToken?: number
    onRetranslateDone?: (pageNum: number, token: number) => void
    tokenDimmed?: (ttsIdx: number) => boolean
  }

  let {
    server,
    bookId,
    seriesId,
    type,
    num,
    subNum,
    pageNum,
    originalTokens,
    translateEnabled = false,
    retranslateToken = 0,
    onRetranslateDone,
    tokenDimmed,
  }: Props = $props()

  let rootEl = $state<HTMLElement | null>(null)
  let isNearViewport = $state(false)
  let translatedTokens = $state<Token[] | null>(null)
  let translating = $state(false)
  let translatePhase = $state<'queued' | 'active' | null>(null)
  let queuePosition = $state(0)
  let translateError = $state(false)
  let loadedTranslateKey = $state<string | null>(null)
  let lastHandledRetranslateToken = $state(0)

  /** Generation token so stale async completions from a prior load are ignored. */
  let loadGen = 0
  let observer: IntersectionObserver | null = null

  const TRANSLATE_ESTIMATE_SEC = 20
  let translateRemainingSec = $state(TRANSLATE_ESTIMATE_SEC)
  let translateEstimateExceeded = $state(false)
  let estimateTimer: ReturnType<typeof setInterval> | null = null

  const estimateCountdownPct = $derived(
    Math.max(0, (translateRemainingSec / TRANSLATE_ESTIMATE_SEC) * 100),
  )

  const cacheId = $derived(bookId || `${seriesId}:${type}:${num}:${subNum}`)
  const translateKey = $derived(`${cacheId}:${pageNum}:t`)

  const displayTokens = $derived(
    translateEnabled && translatedTokens ? translatedTokens : originalTokens,
  )

  const showOverlay = $derived(translateEnabled && translating && !translatedTokens)
  const isQueued = $derived(translatePhase === 'queued')
  const isActive = $derived(translatePhase === 'active')

  function stopEstimateTimer() {
    if (estimateTimer) {
      clearInterval(estimateTimer)
      estimateTimer = null
    }
    translateRemainingSec = TRANSLATE_ESTIMATE_SEC
    translateEstimateExceeded = false
  }

  function startEstimateTimer() {
    stopEstimateTimer()
    const startedAt = Date.now()
    estimateTimer = setInterval(() => {
      const elapsedSec = (Date.now() - startedAt) / 1000
      if (elapsedSec >= TRANSLATE_ESTIMATE_SEC) {
        translateEstimateExceeded = true
        translateRemainingSec = 0
        return
      }
      translateRemainingSec = Math.max(1, Math.ceil(TRANSLATE_ESTIMATE_SEC - elapsedSec))
    }, 200)
  }

  function applyTranslatedText(text: string) {
    translatedTokens = marked.lexer(text)
    loadedTranslateKey = translateKey
    translateError = false
    translating = false
    translatePhase = null
    queuePosition = 0
  }

  function onQueueStatus(status: NovelTranslateQueueStatus) {
    translatePhase = status.phase
    queuePosition = status.position
    translating = true
  }

  function resetTranslateUi() {
    translating = false
    translatePhase = null
    queuePosition = 0
  }

  async function loadTranslation(force = false) {
    if (!translateEnabled || !server || !cacheId) {
      if (!translateEnabled) {
        translatedTokens = null
        loadedTranslateKey = null
      }
      resetTranslateUi()
      translateError = false
      return
    }

    // Only *start* work when near the viewport. Existing jobs keep running
    // after virtual-list unmount; remount reattaches via the shared job map.
    if (!force && !isNearViewport) {
      const existing = getNovelTranslationJobStatus(cacheId, pageNum)
      if (existing) {
        translating = true
        translatePhase = existing.phase
        queuePosition = existing.position
      } else {
        resetTranslateUi()
      }
      return
    }

    if (!force && loadedTranslateKey === translateKey && translatedTokens) {
      return
    }

    if (!force) {
      const cached = getCachedNovelTranslation(cacheId, pageNum)
      if (cached != null) {
        applyTranslatedText(cached)
        return
      }
    }

    const gen = ++loadGen
    translating = true
    translateError = false

    const existingStatus = getNovelTranslationJobStatus(cacheId, pageNum)
    if (existingStatus) {
      translatePhase = existingStatus.phase
      queuePosition = existingStatus.position
    } else {
      translatePhase = 'queued'
      queuePosition = 0
    }

    const onStatus = (status: NovelTranslateQueueStatus) => {
      if (gen !== loadGen) return
      onQueueStatus(status)
    }

    try {
      const text = await fetchNovelTranslation(
        server,
        cacheId,
        pageNum,
        force,
        {
          bookId,
          seriesId,
          type,
          num,
          subNum,
        },
        onStatus,
      )
      if (gen !== loadGen) return
      if (text == null) {
        translateError = true
        resetTranslateUi()
        return
      }
      applyTranslatedText(text)
    } catch (err) {
      if (gen !== loadGen) return
      if ((err as Error)?.name === 'CanceledError' || (err as Error)?.name === 'AbortError') {
        resetTranslateUi()
        return
      }
      translateError = true
      resetTranslateUi()
    }
  }

  $effect(() => {
    if (!translating || !isActive) {
      stopEstimateTimer()
      return
    }
    startEstimateTimer()
    return () => stopEstimateTimer()
  })

  $effect(() => {
    if (!rootEl) return
    observer?.disconnect()
    observer = new IntersectionObserver(
      (entries) => {
        isNearViewport = entries.some((e) => e.isIntersecting)
      },
      { root: null, rootMargin: '400px 0px', threshold: 0 },
    )
    observer.observe(rootEl)
    return () => {
      observer?.disconnect()
      observer = null
    }
  })

  $effect(() => {
    translateEnabled
    bookId
    pageNum
    isNearViewport

    if (!translateEnabled) {
      loadGen++
      translatedTokens = null
      loadedTranslateKey = null
      resetTranslateUi()
      translateError = false
      return
    }

    if (translateKey && loadedTranslateKey === translateKey && translatedTokens) {
      return
    }

    void loadTranslation(false)
    // Intentionally no abort on cleanup — jobs outlive virtual-list cells.
  })

  $effect(() => {
    const token = retranslateToken
    if (!token || token === lastHandledRetranslateToken) return
    if (!translateEnabled || !server) {
      onRetranslateDone?.(pageNum, token)
      lastHandledRetranslateToken = token
      return
    }

    lastHandledRetranslateToken = token
    translatedTokens = null
    loadedTranslateKey = null
    invalidateNovelTranslationPage(cacheId, pageNum)
    void (async () => {
      await loadTranslation(true)
      onRetranslateDone?.(pageNum, token)
    })()
  })

  onDestroy(() => {
    observer?.disconnect()
    stopEstimateTimer()
    // Bump gen so a late completion does not write into a destroyed cell;
    // do not cancel the shared queue job.
    loadGen++
  })
</script>

<div bind:this={rootEl} class="relative">
  {#each displayTokens as token, ttsIdx (ttsIdx)}
    <div
      class="transition-opacity duration-300"
      style="opacity: {tokenDimmed?.(ttsIdx) ? 0.42 : 1};"
    >
      <SvelteMarkdown
        source={[token]}
        renderers={{
          text: Text,
          link: Link,
          paragraph: Paragraph,
          image: ImageRender,
          heading: Heading,
          list: List,
          listitem: ListItem,
        }}
      />
    </div>
  {/each}

  {#if showOverlay}
    <div
      class="pointer-events-none sticky bottom-3 z-1 mx-auto mt-3 flex w-fit max-w-full justify-center"
      aria-live="polite"
      aria-busy="true"
    >
      <div
        class="flex w-[min(100%,17rem)] flex-col gap-2.5 rounded-lg border border-latte-surface1/70
          bg-latte-mantle/92 px-3.5 py-2.5 shadow-lg backdrop-blur-md
          dark:border-macchiato-surface1/70 dark:bg-macchiato-mantle/92"
      >
        <div class="flex items-center gap-3">
          <div
            class="relative flex size-9 shrink-0 items-center justify-center rounded-md border
              border-latte-mauve/25 bg-latte-mauve/10 dark:border-macchiato-mauve/30
              dark:bg-macchiato-mauve/10"
          >
            {#if isActive}
              <span
                class="absolute inset-0 animate-ping rounded-md bg-latte-mauve/20 dark:bg-macchiato-mauve/25"
                aria-hidden="true"
              ></span>
            {/if}
            <LanguagesIcon
              class="relative size-4 text-latte-mauve dark:text-macchiato-mauve"
              strokeWidth={1.75}
              aria-hidden="true"
            />
          </div>
          <div class="min-w-0 flex-1">
            <p
              class="text-[10px] font-medium uppercase tracking-wider text-latte-surface1
                dark:text-macchiato-surface1"
            >
              translation
            </p>
            <div class="mt-0.5 flex items-center gap-2">
              <Spinner
                class="size-3.5 shrink-0 text-latte-mauve dark:text-macchiato-mauve"
                strokeWidth={2.25}
              />
              <span class="text-sm font-medium text-latte-text dark:text-macchiato-text">
                {#if isQueued}
                  Waiting in queue…
                {:else if translateEstimateExceeded}
                  Taking a bit more time…
                {:else}
                  Translating page…
                {/if}
              </span>
            </div>
          </div>
          {#if isQueued && queuePosition > 0}
            <span
              class="tabular-nums text-xs font-medium text-latte-mauve dark:text-macchiato-mauve"
            >
              #{queuePosition}
            </span>
          {:else if isActive && !translateEstimateExceeded}
            <span
              class="tabular-nums text-xs text-latte-text/55 dark:text-macchiato-text/55"
            >
              ~{translateRemainingSec}s
            </span>
          {/if}
        </div>
        {#if isActive}
          <Progress value={estimateCountdownPct} class="h-1" />
        {/if}
      </div>
    </div>
  {/if}

  {#if translateEnabled && translateError && !translatedTokens}
    <div
      class="mt-3 flex items-center justify-center gap-1.5 text-xs text-latte-peach
        dark:text-macchiato-peach"
      role="alert"
    >
      <AlertCircleIcon class="size-3.5 shrink-0" strokeWidth={1.75} />
      Translation failed — showing original
    </div>
  {/if}
</div>
